"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Loader2, Pause, Play } from "lucide-react";

import type { PodcastEpisode, PodcastSeason } from "@/lib/podcast";
import { setPodcastActive } from "@/lib/podcast-now-playing";
import { regionFromPathname, type Region } from "@/lib/region";
import { resolveRichTextHref } from "@/lib/storyblok-link";
import { useRovingFocus } from "@/lib/use-roving-focus";

import { PodcastCover, seasonHex } from "./podcast-cover";
import { EpisodeBadge } from "./episode-badge";
import { EpisodeDialog } from "./episode-dialog";
import {
  EpisodePlayer,
  StickyNowPlaying,
  usePlayerState,
  usePlayerActions,
} from "./podcast-player";
import {
  PodcastPlatformIcon,
  type PodcastPlatformId,
} from "./podcast-platform-icon";

export type PodcastHost = {
  name: string;
  role: string;
  photo: { filename: string; alt: string } | null;
};

export type PodcastPlatformLink = {
  id: PodcastPlatformId;
  label: string;
  href: string;
};


type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string };
};
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
export type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

export type PodcastPageViewProps = {
  title: string;
  tagline: string;
  description: string | RichTextDoc;
  aboutHeading: string;
  aboutImage: { filename: string; alt: string } | null;
  aboutVideoUrl: string;
  hosts: PodcastHost[];
  platforms: PodcastPlatformLink[];
  seasons: PodcastSeason[];
  latestEpisode: PodcastEpisode | null;
};

export default function PodcastPageView(props: PodcastPageViewProps) {

  return (
    <>
      <PodcastPageInner {...props} />
      <StickyNowPlaying />
    </>
  );
}

function PodcastPageInner({
  title,
  tagline,
  description,
  aboutHeading,
  aboutImage,
  aboutVideoUrl,
  hosts,
  platforms,
  seasons,
  latestEpisode,
}: PodcastPageViewProps) {

  const region = regionFromPathname(usePathname() ?? "/");
  const state = usePlayerState();
  const actions = usePlayerActions();


  useEffect(() => {
    setPodcastActive(Boolean(state.episode));
    return () => {
      setPodcastActive(false);
    };
  }, [state.episode]);
  const [activeSeasonNumber, setActiveSeasonNumber] = useState<number | null>(
    seasons[0]?.number ?? null,
  );
  const seasonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { ref: seasonNavRef, rovingProps: seasonRovingProps } =
    useRovingFocus<HTMLDivElement>({ resetOnBlur: false });
  const activeSeason = useMemo(
    () => seasons.find((s) => s.number === activeSeasonNumber) ?? seasons[0],
    [seasons, activeSeasonNumber],
  );

  const selectSeason = (number: number, index: number) => {
    setActiveSeasonNumber(number);
    seasonRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  const onPlayLatest = () => {
    if (!latestEpisode) return;
    actions.toggle(latestEpisode, latestEpisode.season);
  };

  const showPlayerSpacer = Boolean(state.episode);

  return (
    <>
      <section className="bg-dusk-01">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,5rem)]">
          <div className="grid gap-10 md:grid-cols-[1.05fr_minmax(0,1fr)] md:items-center lg:gap-16 xl:gap-20">
            <div>
              <h1 className="text-purple-05 text-[clamp(2.25rem,5vw,4rem)] leading-[1.06] font-bold">
                {title}
              </h1>
              {tagline && (
                <p className="mt-5 max-w-[560px] text-[clamp(1rem,1.6vw,1.2rem)] leading-[1.6] text-black/65">
                  {tagline}
                </p>
              )}

              {latestEpisode && (
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  {(() => {
                    const isLatestLoaded = state.episodeId === latestEpisode.id;
                    const showLoading = isLatestLoaded && state.loading;
                    const showPause =
                      isLatestLoaded && state.playing && !state.loading;
                    return (
                      <button
                        type="button"
                        onClick={onPlayLatest}
                        aria-busy={showLoading || undefined}
                        className="bg-purple-03 hover:bg-purple-04 inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[8px] px-5 text-body font-bold text-white transition-colors md:h-14 md:px-7"
                      >
                        {showLoading ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : showPause ? (
                          <Pause className="size-4 fill-current" />
                        ) : (
                          <Play className="size-4 fill-current" />
                        )}
                        {showLoading
                          ? "Loading…"
                          : showPause
                            ? "Pause"
                            : "Play latest episode"}
                      </button>
                    );
                  })()}
                </div>
              )}

              {platforms.length > 0 && (
                <ul className="mt-12 flex flex-wrap items-center gap-2 lg:mt-16">
                  {platforms.map((p) => (
                    <li key={p.id}>
                      <a
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-05 hover:bg-purple-03 hover:text-white inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-2 text-button font-semibold transition-colors"
                      >
                        <PodcastPlatformIcon id={p.id} className="size-4" />
                        {p.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="relative mx-auto w-full max-w-[320px] sm:max-w-[400px] md:max-w-none">
              {/* p-[5%] gives the back cards room to fan out without
                  spilling past the column on small screens. */}
              <div className="aspect-square w-full p-[5%]">
                <PodcastCover
                  title={title}
                  seasons={seasons}
                  latestEpisode={latestEpisode}
                  className="block size-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      {(description || hosts.length > 0) && (
        <section className="bg-white">
          <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,6vw,5rem)]">
            <div
              className={`grid gap-10 md:gap-12 lg:gap-16 xl:gap-20 ${
                aboutImage || aboutVideoUrl
                  ? "md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]"
                  : ""
              }`}
            >
              {(aboutImage || aboutVideoUrl) && (
                <AboutMedia image={aboutImage} videoUrl={aboutVideoUrl} />
              )}

              <div className="flex flex-col md:h-full">
                <h2 className="text-purple-05 text-[clamp(1.75rem,3vw,2.25rem)] leading-[1.12] font-bold">
                  {aboutHeading}
                </h2>
                <AboutDescription value={description} region={region} />

                {hosts.length > 0 && (
                  <>
                    <div className="mt-9 text-[12px] font-bold uppercase tracking-[0.12em] text-black/55 md:mt-auto md:pt-9">
                      Your hosts
                    </div>
                    <ul className="mt-4 flex flex-wrap gap-3">
                      {hosts.map((h, i) => (
                        <li
                          key={`${h.name}-${i}`}
                          className="bg-dusk-01 ring-1 ring-purple-01/50 flex w-fit max-w-full items-center gap-4 rounded-[16px] p-3 pr-6 sm:p-4 sm:pr-7"
                        >
                          <div className="bg-purple-01 size-14 shrink-0 overflow-hidden rounded-full shadow-sm ring-2 ring-white sm:size-16">
                            {h.photo?.filename ? (
                              <Image
                                src={h.photo.filename}
                                alt={h.photo.alt || h.name}
                                width={128}
                                height={128}
                                className="size-full object-cover"
                              />
                            ) : (
                              <div className="flex size-full items-center justify-center text-h6 font-bold text-purple-05">
                                {initials(h.name)}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-h6 font-bold text-purple-05">
                              {h.name}
                            </div>
                            {h.role && (
                              <div className="text-button text-black/60">
                                {h.role}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {seasons.length > 0 && (
        <section id="seasons" className="bg-dusk-01 scroll-mt-24">
          <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,6vw,5rem)]">
            <div className="flex flex-col gap-3">
              <h2 className="text-purple-05 text-[clamp(1.75rem,3vw,2.25rem)] leading-[1.12] font-bold">
                Browse by season
              </h2>
              <p className="max-w-[560px] text-body text-black/65">
                {seasons.length} seasons,{" "}
                {seasons.reduce((n, s) => n + s.episodes.length, 0)}{" "}
                conversations. Pick where you want to start.
              </p>
            </div>

            <div className="scrollbar-none mt-7 -mx-[clamp(1.25rem,4vw,2.5rem)] overflow-x-auto px-[clamp(1.25rem,4vw,2.5rem)] [-ms-overflow-style:none] md:mx-0 md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
              <div
                ref={seasonNavRef}
                role="toolbar"
                aria-label="Browse seasons"
                className="flex w-max gap-2 md:w-auto md:flex-wrap"
                {...seasonRovingProps}
              >
                {seasons.map((s, i) => {
                  const isActive = s.number === activeSeason?.number;
                  const color = seasonHex(s.number);
                  return (
                    <button
                      key={s.number}
                      ref={(el) => {
                        seasonRefs.current[i] = el;
                      }}
                      data-roving=""
                      aria-pressed={isActive}
                      onClick={() => selectSeason(s.number, i)}
                      style={{
                        background: isActive ? color : undefined,
                        borderColor: isActive ? color : undefined,
                      }}
                      className={`flex h-11 shrink-0 items-center gap-2.5 rounded-full border px-5 text-button font-semibold transition-colors ${
                        isActive
                          ? "text-white"
                          : "border-black/10 bg-white text-purple-05 hover:bg-purple-00"
                      }`}
                    >
                      <span
                        style={{
                          background: isActive
                            ? "rgba(255,255,255,0.22)"
                            : color,
                        }}
                        className="flex size-6 items-center justify-center rounded-full text-[12px] font-extrabold text-white"
                      >
                        {s.number}
                      </span>
                      Season {s.number}
                      <span
                        className={`hidden text-[11px] font-medium sm:inline ${
                          isActive ? "text-white/75" : "text-black/55"
                        }`}
                      >
                        · {s.episodes.length} ep
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {activeSeason && (
              <ul className="mt-8 grid grid-cols-1 gap-4 md:mt-10 md:grid-cols-2">
                {activeSeason.episodes.map((ep) => (
                  <EpisodeRow
                    key={ep.id}
                    episode={ep}
                    seasonNumber={activeSeason.number}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      )}


      {/* Spacer so the sticky now-playing bar doesn't cover footer content. */}
      {showPlayerSpacer && <div aria-hidden className="h-[110px] md:h-[88px]" />}
    </>
  );
}

function EpisodeRow({
  episode,
  seasonNumber,
}: {
  episode: PodcastEpisode;
  seasonNumber: number;
}) {
  const state = usePlayerState();
  const isCurrent = state.episodeId === episode.id;
  const [open, setOpen] = useState(false);

  const showReadMore = (episode.description?.length ?? 0) > 200;

  return (
    <>
      <li
        className={`relative flex flex-col gap-4 rounded-[16px] border bg-white p-5 transition-colors sm:p-6 ${
          isCurrent
            ? "border-purple-03 shadow-[0_18px_40px_-24px_rgba(60,30,160,0.45)]"
            : "border-black/8"
        }`}
      >
        <div className="flex items-start gap-4 sm:gap-5">
          <EpisodeBadge
            seasonNumber={seasonNumber}
            episodeNumber={episode.number}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] font-semibold tracking-[0.04em] text-black/55">
              <span className="text-purple-03 uppercase">
                Season {seasonNumber}
              </span>
              {episode.formattedDate && (
                <>
                  <span className="text-black/30">·</span>
                  <span>{episode.formattedDate}</span>
                </>
              )}
            </div>
            <h3 className="text-h6 leading-tight font-bold text-purple-05">
              {episode.title}
            </h3>
          </div>
        </div>

        {episode.description && (
          <p className="line-clamp-3 text-body leading-[1.6] text-black/65">
            {episode.description}
          </p>
        )}

        {showReadMore && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-purple-03 -mt-1 self-start text-button font-semibold hover:underline"
          >
            Read more
          </button>
        )}

        <EpisodePlayer episode={episode} seasonNumber={seasonNumber} />
      </li>
      {open && (
        <EpisodeDialog
          episode={episode}
          seasonNumber={seasonNumber}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}


function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function AboutDescription({
  value,
  region,
}: {
  value: string | RichTextDoc;
  region?: Region;
}) {
  const baseClass =
    "mt-5 text-[clamp(1rem,1.3vw,1.1875rem)] leading-[1.6] text-black/75";
  if (typeof value === "string") {
    if (!value.trim()) return null;
    return <p className={baseClass}>{value}</p>;
  }
  if (!richTextHasContent(value)) return null;
  return (
    <div className={`flex flex-col gap-4 ${baseClass}`}>
      {value.content?.map((n, i) => renderRichNode(n, i, region))}
    </div>
  );
}

function richTextHasContent(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(richTextHasContent);
}

function renderRichNode(
  node: RichTextNode,
  key: number,
  region?: Region,
): React.ReactNode {
  switch (node.type) {
    case "paragraph":
      return (
        <p key={key}>
          {node.content?.map((n, i) => renderRichNode(n, i, region))}
        </p>
      );
    case "hard_break":
      return <br key={key} />;
    case "bullet_list":
      return (
        <ul key={key} className="ml-5 list-disc space-y-1">
          {node.content?.map((n, i) => renderRichNode(n, i, region))}
        </ul>
      );
    case "ordered_list":
      return (
        <ol key={key} className="ml-5 list-decimal space-y-1">
          {node.content?.map((n, i) => renderRichNode(n, i, region))}
        </ol>
      );
    case "list_item":
      return (
        <li key={key}>
          {node.content?.map((n, i) =>
            n.type === "paragraph"
              ? n.content?.map((c, j) => renderRichNode(c, j, region))
              : renderRichNode(n, i, region),
          )}
        </li>
      );
    case "text": {
      let el: React.ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className="text-purple-03 underline underline-offset-2 hover:opacity-80"
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderRichNode(n, i, region)) ?? null;
  }
}



function AboutMedia({
  image,
  videoUrl,
}: {
  image: { filename: string; alt: string } | null;
  videoUrl: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true; // set the property too — the attribute alone is unreliable
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (videoUrl) {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-[20px] bg-black md:aspect-auto md:h-full">
        <video
          ref={videoRef}
          className="absolute inset-0 size-full object-cover"
          src={videoUrl}
          poster={image?.filename || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden
        />
      </div>
    );
  }

  if (image?.filename) {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-[20px] bg-dusk-01 md:aspect-auto md:h-full">
        <Image
          src={image.filename}
          alt={image.alt || ""}
          fill
          sizes="(min-width: 1115px) 540px, (min-width: 768px) 50vw, 100vw"
          className="object-cover"
        />
      </div>
    );
  }

  return null;
}
